import React, { useEffect, useState } from 'react'
import { MapContainer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { useQuery } from 'react-query'
import { getLocations } from '../api/dashboard-api'
import { DEFAULT_CENTER_POSITION } from '../constants/constants'
import { GetMarkerIconByLocation } from '../../locations/utils/get-marker-icon'
import { PinPopUp } from './pin-popup'
import { OpenFreeMapLayer } from './openfreemap-layer'
import { LocationWithoutAddressTooltip } from './location-without-address-tooltip'
import { OptionSwitcher } from '../../root/components/common/option-switcher/option-switcher'
import { REPRESENTATION } from '../../root/constants'
import { useSelector } from 'react-redux'

export function MapView({ isMobile = false }) {
    const { locale } = useSelector(state => state.main)
    const { data, isLoading } = useQuery('mapview-locations', getLocations)
    const [locationsToDisplay, setLocationsToDisplay] = useState([])
    const [locationsWithCoordinates, setLocationsWithCoordinates] = useState([])
    const [locationsWithoutCoordinates, setLocationsWithoutCoordinates] =
        useState([])
    const [
        locationsWithAddressAndWithoutCoordinates,
        setLocationsWithAddressAndWithoutCoordinates
    ] = useState([])

    const [representationStatus, setRepresentationStatus] = useState(
        REPRESENTATION.all
    )

    useEffect(() => {
        setLocationsWithCoordinates(
            locationsToDisplay.filter(
                location =>
                    location.longitude !== null && location.latitude !== null
            )
        )
        setLocationsWithoutCoordinates(
            locationsToDisplay.filter(
                location =>
                    (location.longitude === null ||
                        location.latitude === null) &&
                    !location.address
            )
        )
        setLocationsWithAddressAndWithoutCoordinates(
            locationsToDisplay.filter(
                location =>
                    (location.longitude === null ||
                        location.latitude === null) &&
                    !!location.address
            )
        )
    }, [locationsToDisplay])

    useEffect(() => {
        if (!isLoading) {
            const newLocationsToDisplay =
                representationStatus === REPRESENTATION.all
                    ? data
                    : data.filter(location => location.is_assigned)
            setLocationsToDisplay(newLocationsToDisplay)
        }
    }, [isLoading, data, representationStatus])

    const centerPostion = [
        DEFAULT_CENTER_POSITION.longitude,
        DEFAULT_CENTER_POSITION.latitude
    ]
    return (
        <>
            {!isLoading && (
                <MapContainer
                    center={centerPostion}
                    zoom={11}
                    zoomControl={false}
                    minZoom={1}
                    maxZoom={20}
                    style={{ position: 'relative' }}>
                    {!isMobile && (
                        <OptionSwitcher
                            options={{
                                [REPRESENTATION.all]:
                                    locale.locations.allLocations,
                                [REPRESENTATION.assigned]:
                                    locale.locations.myLocations
                            }}
                            style={{
                                zIndex: 400,
                                position: 'absolute',
                                right: 80,
                                top: 27
                            }}
                            selected={representationStatus}
                            onChange={setRepresentationStatus}
                        />
                    )}
                    <OpenFreeMapLayer />
                    <BoundsUpdater locations={locationsWithCoordinates} />
                    {!isMobile && (
                        <LocationWithoutAddressTooltip
                            locationsWithoutCoordinates={
                                locationsWithoutCoordinates
                            }
                            locationsWithAddressAndWithoutCoordinates={
                                locationsWithAddressAndWithoutCoordinates
                            }
                        />
                    )}
                    {locationsWithCoordinates.map(location => {
                        return (
                            <Marker
                                key={location.id}
                                position={[
                                    location.latitude,
                                    location.longitude
                                ]}
                                icon={GetMarkerIconByLocation(location)}>
                                <Popup>
                                    <PinPopUp
                                        location={location}
                                        isMobile={isMobile}
                                    />
                                </Popup>
                            </Marker>
                        )
                    })}
                    <CustomZoomControl position="bottomright" />
                </MapContainer>
            )}
        </>
    )
}
function BoundsUpdater({ locations }) {
    const map = useMap()

    useEffect(() => {
        if (locations.length > 0) {
            const bounds = L.latLngBounds(
                locations.map(location => [
                    location.latitude,
                    location.longitude
                ])
            )
            map.fitBounds(bounds)
        }
    }, [locations, map])

    return null
}

function CustomZoomControl({ position }) {
    const map = useMap()

    useEffect(() => {
        const zoomControl = L.control.zoom({
            position: position,
            zoomInText: '+',
            zoomOutText: '-',
            zoomInTitle: 'Zoom in',
            zoomOutTitle: 'Zoom out'
        })

        zoomControl.addTo(map)

        return () => {
            zoomControl.remove()
        }
    }, [map, position])

    return null
}
